import { useCallback, useMemo } from 'react';

import { join, map, pick } from 'lodash-es';

import { CoreLaneRowConfiguration, CoreLaneRowType } from '../components/CoreLane/CoreLaneRows';
import { DEFAULT_MAX_RESOLUTION, LANE_RULER_HEIGHT_PX, LANE_RULER_SCALE_THRESHOLD } from '../types/constants';
import { ImageTypeLoaders } from '../types/imageTypeLoader';
import { LaneDimension, LaneThickness } from '../types/imageTypeMetadata';
import { LengthRange } from '../types/lengthRange';

export type LaneThicknessProvider = (scale: number) => LaneThickness[];

export interface UseMergedImageTypeLoadersProps {
	mergedLaneThickness: LaneThicknessProvider;
	mergedRange: LengthRange | undefined;
	highestAvailableResolution: number;
}

const DefaultIntervalRowThickness: LaneThickness = {
	variableM: 0.01,
	minPx: 5,
	maxPx: 50,
};

const applyLaneThicknessThresholds = (thickness: LaneThickness, scale?: number): LaneThickness => {
	// Don't show if we cross the threshold
	if (scale && (thickness.threshold ?? 0) > scale) return thickness;

	// If we have a min/max size in px (and a scale) then convert to fixed size at the min/max points
	let fixedComponent = thickness.fixedPx ?? 0;
	let variableComponent = thickness.variableM ?? 0;
	if (variableComponent && scale) {
		if (thickness.minPx && variableComponent * scale < thickness.minPx) {
			fixedComponent = fixedComponent + thickness.minPx;
			variableComponent = 0;
		}
		if (thickness.maxPx && variableComponent * scale > thickness.maxPx) {
			fixedComponent = fixedComponent + thickness.maxPx;
			variableComponent = 0;
		}
	}

	return {
		fixedPx: fixedComponent,
		variableM: variableComponent,
	};
};

const getLaneRowSizeKey = (config: CoreLaneRowConfiguration): string => {
	switch (config.type) {
		case CoreLaneRowType.Image: {
			return JSON.stringify(pick(config, ['imageTypeId', 'isUncropped']));
		}
		case CoreLaneRowType.Interval: {
			return JSON.stringify(pick(config, ['thickness', 'intervals']));
		}
	}
	return 'no-op';
};

/**
 * Convert a configuration object to fixed and variable width
 * @param config Lane row configuration
 * @param loader Image loader (used for image row types
 * @param scale optional scale to set for threshold calculations
 */
export const laneRowSizeProvider = (
	config: CoreLaneRowConfiguration,
	loaders: ImageTypeLoaders,
	scale?: number,
): LaneDimension | undefined => {
	switch (config.type) {
		case CoreLaneRowType.Image: {
			const metadata = loaders[config.imageTypeId]?.imageTypeMetadata;
			if (!metadata) return undefined;
			return {
				thickness: { variableM: metadata.width },
				range: {
					depthFrom: metadata.depthFrom,
					// Use the uncropped when the image requires it, and it is available
					depthTo: (config.isUncropped && metadata.trayTo) || metadata.depthTo,
				},
			};
		}
		case CoreLaneRowType.Interval:
			return {
				thickness: applyLaneThicknessThresholds(config.thickness ?? DefaultIntervalRowThickness, scale),
				// Intervals shouldn't set the overall range, so return undefined
				range: undefined,
			};
		case CoreLaneRowType.Ruler:
			// A ruler has no range, as it is a "passive" display element
			return {
				thickness: { fixedPx: LANE_RULER_HEIGHT_PX, threshold: LANE_RULER_SCALE_THRESHOLD },
			};

		case CoreLaneRowType.Numeric:
			return {
				thickness: applyLaneThicknessThresholds(config.thickness ?? DefaultIntervalRowThickness, scale),
				// Intervals shouldn't set the overall range, so return undefined
				range: undefined,
			};
	}
};

export const useMergedImageTypeLoaders = (
	loaders: ImageTypeLoaders,
	configurations: CoreLaneRowConfiguration[][],
): UseMergedImageTypeLoadersProps => {
	const activeMetadata = useMemo(() => {
		return Object.keys(loaders)
			.map((x) => parseInt(x))
			.map((x) => loaders[x]);
	}, [loaders]);

	const configurationsKey = configurations.reduce(
		(previousValue, currentValue) =>
			previousValue +
			'|' +
			currentValue.reduce(
				(prevInternal, currInternal) => prevInternal + '|' + getLaneRowSizeKey(currInternal),
				'',
			),
		'',
	);

	// Use a signature of loaded in configurations to prevent the heights in the hook below from constantly rendering
	const imageLoaderSignature = useMemo(
		() =>
			join(
				map(loaders, (value, prop) => ({ imageTypeId: +prop, metadata: value?.imageTypeMetadata }))
					.filter((l) => l.metadata)
					.map((l) => l.imageTypeId)
					.sort(),
				'|',
			),
		[loaders],
	);

	const mergedPanelDimensions = useMemo(() => {
		return configurations?.map<LaneDimension | undefined>((config) => {
			// Resolve outer merged dimensions (i.e: for each pane)
			return config.reduce<LaneDimension | undefined>((innerPrev, innerCurr) => {
				const innerDimensions = laneRowSizeProvider(innerCurr, loaders);
				if (!innerDimensions) return innerPrev;
				const thickness = innerDimensions.thickness;
				return {
					thickness: {
						fixedPx: (innerPrev?.thickness.fixedPx ?? 0) + (thickness.fixedPx ?? 0),
						variableM: (innerPrev?.thickness.variableM ?? 0) + (thickness.variableM ?? 0),
					},
					range: innerDimensions?.range
						? {
								depthFrom: innerPrev?.range
									? Math.min(innerPrev.range.depthFrom, innerDimensions.range.depthFrom)
									: innerDimensions.range.depthFrom,
								depthTo: innerPrev?.range
									? Math.max(innerPrev.range.depthTo, innerDimensions.range.depthTo)
									: innerDimensions.range.depthTo,
							}
						: innerPrev?.range,
				};
			}, undefined);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [configurationsKey, imageLoaderSignature]);

	const highestAvailableResolution = useMemo(() => {
		const resolutions = Array.from(new Set(activeMetadata.flatMap((x) => x.imageTypeMetadata?.resolutions ?? [])));
		return resolutions.length > 0 ? resolutions.reduce((a, b) => Math.max(a, b), 0) : DEFAULT_MAX_RESOLUTION;
	}, [activeMetadata]);

	// Given the range has no variation from scale, we can get the total bounds of our configuration
	const mergedRangeInternal = mergedPanelDimensions
		? mergedPanelDimensions.reduce<LengthRange | undefined>(
				(prev, curr) =>
					curr?.range
						? {
								depthFrom: prev ? Math.min(prev.depthFrom, curr.range.depthFrom) : curr.range.depthFrom,
								depthTo: prev ? Math.max(prev.depthTo, curr.range.depthTo) : curr.range.depthTo,
							}
						: prev,
				undefined,
			)
		: undefined;

	const mergedRange = useMemo(() => {
		return mergedRangeInternal;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mergedRangeInternal?.depthFrom, mergedRangeInternal?.depthTo]);

	const mergedLaneThickness: LaneThicknessProvider = useCallback(
		(scale: number) => {
			return configurations?.map<LaneThickness>(
				(config) => {
					// Resolve outer merged dimensions (i.e: for each pane)
					return config.reduce<LaneThickness>(
						(innerPrev, innerCurr) => {
							const innerDimensions = laneRowSizeProvider(innerCurr, loaders, scale);
							if (!innerDimensions) return innerPrev;
							const thickness = innerDimensions.thickness;

							if (scale && scale < (thickness?.threshold ?? 0)) return innerPrev;

							return {
								fixedPx: (innerPrev.fixedPx ?? 0) + (thickness.fixedPx ?? 0),
								variableM: (innerPrev.variableM ?? 0) + (thickness.variableM ?? 0),
							};
						},
						{ fixedPx: 0, variableM: 0 },
					);
				},
				{ width: 0, depthFrom: 0, depthTo: 0 },
			);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[configurationsKey, imageLoaderSignature],
	);

	return { mergedLaneThickness, mergedRange, highestAvailableResolution };
};
